import { useCallback } from 'react';
import { useAppSelector } from 'src/app/hooks';
import { useDispatch } from 'react-redux';
import useObjectManagement from 'src/services/useObjectManagement';
import { concatUniqueObjectsById } from 'src/utils/concatUniqueObjectsById';
import { useAlert } from 'src/hooks/useAlert';
import { reducerUpdateObjectListByObjectType } from 'src/features/objectManagement';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';

const useRefreshObjectList = () => {
  const dispatch = useDispatch();
  const { projectId } = useAppSelector((state) => ({
    projectId: state.users.project.id
  }));

  const { objectIdList } = useAppSelector((state) => state.analyticArguments);

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );

  const { groupSelectedById: selectedGroupId, selectedGroups } = useAppSelector(
    (state) => state.objectManagement
  );

  const { getObjectList, getGroupObject } = useObjectManagement();
  const { handleClickAlert } = useAlert();

  const objectIds = objectList.map((object) => object.id);

  const refreshObjectList = useCallback(() => {
    Promise.all([
      getObjectList(projectId),
      getGroupObject({ projectId: projectId, templateName: 'content' })
    ])
      .then((responses) => {
        // if groups is selected
        if (selectedGroupId !== '_default' || selectedGroups.length > 0) {
          const newGroupObjectList = responses[1].data;
          const prevSelectedGroups = selectedGroups;

          const prevSelectedGroupObjectListIds = prevSelectedGroups
            .map((group) => group.objectList.map((group) => group.id))
            .flat();

          const newSelectedGroups = newGroupObjectList.filter((group) =>
            prevSelectedGroups.some((prevGroup) => prevGroup.id === group.id)
          );

          const allObjectsFromGroups = newSelectedGroups.flatMap(
            (group) => group.objectList
          );

          // all active object + new objects from group
          const finalActiveObjects = allObjectsFromGroups.filter(
            (object) =>
              objectIdList.includes(object.id) ||
              !prevSelectedGroupObjectListIds.includes(object.id)
          );
          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
          // if no group selected
        } else {
          const newObjectList = responses[0].data;
          // get only new objects
          const newObjects = newObjectList.filter((object) => {
            return !objectIds.includes(object.id);
          });

          const selectedObjects = newObjectList.filter((object) =>
            objectIdList.includes(object.id)
          );
          const finalActiveObjects = concatUniqueObjectsById(
            selectedObjects,
            newObjects
          );

          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
        }
      })
      .catch((error) =>
        handleClickAlert({
          vertical: 'top',
          horizontal: 'center',
          message: "Couldn't fetch data, please try again.",
          severity: 'error'
        })
      );
  }, [
    projectId,
    getObjectList,
    getGroupObject,
    objectIdList,
    selectedGroupId,
    selectedGroups,
    objectIds,
    dispatch,
    handleClickAlert
  ]);

  return refreshObjectList;
};

export default useRefreshObjectList;
